import { useSyncExternalStore } from 'react'

/** A small JSON value in localStorage that components can subscribe to. Private mode falls back to memory. */
export function localStore<T>(key: string, initial: T) {
  let mem: T | null = null
  const listeners = new Set<() => void>()
  const read = (): T => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : initial
    } catch {
      return mem ?? initial
    }
  }
  let cache = read()
  const store = {
    get: () => cache,
    set(v: T) {
      cache = v
      mem = v
      try {
        localStorage.setItem(key, JSON.stringify(v))
      } catch {
        /* memory only */
      }
      listeners.forEach((l) => l())
    },
    subscribe(l: () => void) {
      listeners.add(l)
      const onStorage = (e: StorageEvent) => {
        if (e.key === key || e.key === null) {
          cache = read()
          l()
        }
      }
      window.addEventListener('storage', onStorage)
      return () => {
        listeners.delete(l)
        window.removeEventListener('storage', onStorage)
      }
    },
  }
  return store
}

export function useLocal<T>(store: ReturnType<typeof localStore<T>>) {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}
